import * as z from 'zod';

export const CreateOperationGroupRequest = z.object({
  operationGroup: z
    .object({
      _id: z
        .string()
        .describe('OperationsGroup ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the OperationsGroup is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the OperationsGroup.\n\nIgnored when creating a OperationsGroup.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the OperationsGroup was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the OperationsGroup was last updated.')
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('The name of the operations group')
        .min(1)
        .max(500),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data Extensions')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operation groups.'
        )
        .optional(),
    })
    .describe('OperationGroup to be created.'),
});
export const CreateOperationGroupResponse = z.object({
  _id: z
    .string()
    .describe('OperationsGroup ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the OperationsGroup is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the OperationsGroup.\n\nIgnored when creating a OperationsGroup.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the OperationsGroup was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the OperationsGroup was last updated.')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('The name of the operations group')
    .min(1)
    .max(500)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Data Extensions')
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operation groups.'
    )
    .optional(),
});
export const GetOperationGroupRequest = z.object({
  operationGroupId: z
    .string()
    .describe('ID of the OperationGroup to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetOperationGroupResponse = z.object({
  _id: z
    .string()
    .describe('OperationsGroup ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the OperationsGroup is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the OperationsGroup.\n\nIgnored when creating a OperationsGroup.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the OperationsGroup was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the OperationsGroup was last updated.')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('The name of the operations group')
    .min(1)
    .max(500)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Data Extensions')
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operation groups.'
    )
    .optional(),
});
export const UpdateOperationGroupRequest = z.object({
  _id: z
    .string()
    .describe('OperationsGroup ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  operationGroup: z
    .object({
      _id: z
        .string()
        .describe('OperationsGroup ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the OperationsGroup is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the OperationsGroup.\n\nIgnored when creating a OperationsGroup.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the OperationsGroup was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the OperationsGroup was last updated.')
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('The name of the operations group')
        .min(1)
        .max(500)
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data Extensions')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operation groups.'
        )
        .optional(),
    })
    .describe('OperationGroup to be updated, may be partial.'),
});
export const UpdateOperationGroupResponse = z.object({
  _id: z
    .string()
    .describe('OperationsGroup ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the OperationsGroup is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the OperationsGroup.\n\nIgnored when creating a OperationsGroup.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the OperationsGroup was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the OperationsGroup was last updated.')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('The name of the operations group')
    .min(1)
    .max(500)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Data Extensions')
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operation groups.'
    )
    .optional(),
});
export const DeleteOperationGroupRequest = z.object({
  operationGroupId: z
    .string()
    .describe('Id of the OperationGroup to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteOperationGroupResponse = z.object({});
export const QueryOperationGroupsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('WQL expression.'),
});
export const QueryOperationGroupsResponse = z.object({
  operationGroups: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('OperationsGroup ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the OperationsGroup is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the OperationsGroup.\n\nIgnored when creating a OperationsGroup.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the OperationsGroup was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the OperationsGroup was last updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('The name of the operations group')
          .min(1)
          .max(500)
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Data Extensions')
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operation groups.'
          )
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata')
    .optional(),
});
export const BulkCreateOperationGroupsRequest = z.object({
  operationGroups: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('OperationsGroup ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the OperationsGroup is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the OperationsGroup.\n\nIgnored when creating a OperationsGroup.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the OperationsGroup was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the OperationsGroup was last updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('The name of the operations group')
          .min(1)
          .max(500),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Data Extensions')
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operation groups.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'set to `true` if you wish to receive back the created OperationGroups in the response'
        )
        .optional(),
    })
    .optional(),
});
export const BulkCreateOperationGroupsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata regarding the specific single create operation')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('OperationsGroup ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the OperationsGroup is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the OperationsGroup.\n\nIgnored when creating a OperationsGroup.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the OperationsGroup was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the OperationsGroup was last updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('The name of the operations group')
              .min(1)
              .max(500)
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Data Extensions')
              .optional(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                tags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe(
                'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operation groups.'
              )
              .optional(),
          })
          .describe(
            'Only exists if `returnEntity` was set to true in the request'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata regarding the bulk create operation')
    .optional(),
});
export const BulkUpdateOperationGroupsRequest = z.object({
  operationGroups: z
    .array(
      z.object({
        operationGroup: z
          .object({
            _id: z
              .string()
              .describe('OperationsGroup ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the OperationsGroup is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the OperationsGroup.\n\nIgnored when creating a OperationsGroup.'
              ),
            _createdDate: z
              .date()
              .describe('Date and time the OperationsGroup was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the OperationsGroup was last updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('The name of the operations group')
              .min(1)
              .max(500)
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Data Extensions')
              .optional(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                tags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe(
                'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operation groups.'
              )
              .optional(),
          })
          .describe('OperationGroup to be updated, may be partial')
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'set to `true` if you wish to receive back the updated OperationGroups in the response'
        )
        .optional(),
    })
    .optional(),
});
export const BulkUpdateOperationGroupsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata regarding the specific single update operation')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('OperationsGroup ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the OperationsGroup is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the OperationsGroup.\n\nIgnored when creating a OperationsGroup.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the OperationsGroup was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the OperationsGroup was last updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('The name of the operations group')
              .min(1)
              .max(500)
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Data Extensions')
              .optional(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                tags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe(
                'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operation groups.'
              )
              .optional(),
          })
          .describe(
            'Only exists if `returnEntity` was set to true in the request'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata regarding the bulk update operation')
    .optional(),
});
export const BulkDeleteOperationGroupsRequest = z.object({
  operationGroupIds: z.array(z.string()).min(1).max(100),
});
export const BulkDeleteOperationGroupsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata regarding the specific single delete operation')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata regarding the bulk delete operation')
    .optional(),
});
export const BulkUpdateOperationGroupTagsRequest = z.object({
  operationGroupIds: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the operation groups.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the operation groups.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateOperationGroupTagsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the updated operation group.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the bulk update.')
    .optional(),
});
export const BulkUpdateOperationGroupTagsByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe(
      'Filter that determines which operation groups to update tags for.'
    ),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the operation groups.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the operation groups.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateOperationGroupTagsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      "Job ID. Pass this ID to Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
